import { CloseOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Card, Col, Row, Space, Typography } from "antd";
import { useTable } from "components/UI/Table/hooks/useTable";
import { usePaldockTranslation } from "hooks/usePaldockTranslation.tsx";
import { useEffect, useRef } from "react";
import { useRecoilState } from "recoil";
import { SearchFilterEnum } from "types/enums";
import { Identifiable, TableId } from "types/models";

import { FilterItem } from "./FilterItem/FilterItem";
import { filterAtom } from "../../atoms/filterAtom";
import { FilterRule, FilterType } from "../../types/filter";
import { TableColumnType } from "../../types/table";

interface FilterProps<T> {
    columnConfig: TableColumnType<T>[];
    tableId: TableId;
    extraFilters?: Array<{
        dataIndex: string;
        title: string;
        filterType: FilterType;
        filterOptions?: { label: string; value: string | number | boolean }[];
        filterName?: string;
        filterIncludeNull?: boolean;
        filterNullOptionLabel?: string;
    }>;
}

export const Filter = <T extends Identifiable>({ columnConfig, tableId, extraFilters = [] }: FilterProps<T>) => {
    const { t } = usePaldockTranslation("tables.ui.filter");
    const { t: tC } = usePaldockTranslation("common");
    const [filters, setFilters] = useRecoilState(filterAtom(tableId));
    const tableUtils = useTable<T>(tableId);

    const withNullOperator = (filterType: FilterType, includeNull?: boolean): FilterType =>
        includeNull && !filterType.operators.includes(SearchFilterEnum.Null)
            ? { ...filterType, operators: [SearchFilterEnum.Null, ...filterType.operators] }
            : filterType;

    const columnFilters = columnConfig
        .filter((col) => col.filterable && col.filterType)
        .map((col) => ({
            dataIndex: col.dataIndex as string,
            title: col.title as string,
            filterType: withNullOperator(col.filterType as FilterType, col.filterIncludeNull),
            filterOptions: col.filterOptions,
            filterName: col.filterName,
            filterIncludeNull: col.filterIncludeNull,
            filterNullOptionLabel: col.filterNullOptionLabel,
        }));

    const allFilterColumns = [...columnFilters, ...extraFilters];

    // Only string-type filters can be added multiple times, others only once
    const usedNonStringDataIndices = filters.filter((f) => f.filterType.type !== "string").map((f) => f.dataIndex);
    const availableFilterColumns = allFilterColumns.filter(
        (col) => col.filterType.type === "string" || !usedNonStringDataIndices.includes(col.dataIndex)
    );

    const didInitRef = useRef(false);

    useEffect(() => {
        if (!didInitRef.current && filters.length === 0 && availableFilterColumns.length) {
            didInitRef.current = true;
            addFilter();
        }
    }, [filters.length, availableFilterColumns.length]);

    const addFilter = () => {
        if (!availableFilterColumns.length) return;
        const col = availableFilterColumns[0];

        setFilters((prev) => [
            ...prev,
            {
                id: Date.now(),
                dataIndex: col.dataIndex,
                filterName: col.filterName ?? col.dataIndex,
                filterType: withNullOperator(col.filterType, col.filterIncludeNull),
                filterOptions: col.filterOptions || [],
                filterIncludeNull: col.filterIncludeNull,
                filterNullOptionLabel: col.filterNullOptionLabel,
                operator: col.filterType.operators[0] as SearchFilterEnum,
                value: defaultValueForType(col.filterType.type),
            },
        ]);
    };

    const updateFilter = (id: number, key: keyof FilterRule, value: unknown) => {
        setFilters((prev) =>
            prev.map((f) => {
                if (f.id !== id) return f;

                if (key === "dataIndex") {
                    const newColumn = allFilterColumns.find((col) => col.dataIndex === value);
                    if (!newColumn) return f;

                    const oldType = f.filterType.type;
                    const newType = newColumn.filterType.type;

                    // Check if types are compatible to preserve value
                    const isCompatible =
                        oldType === newType ||
                        (oldType === "number" && newType === "string") ||
                        (["date", "datetime"].includes(oldType) && ["date", "datetime"].includes(newType));

                    const nextFilterType = withNullOperator(newColumn.filterType, newColumn.filterIncludeNull);

                    // Check if old operator is available in new column
                    const operatorAvailable = nextFilterType.operators.includes(f.operator);
                    const op = operatorAvailable ? f.operator : (nextFilterType.operators[0] as SearchFilterEnum);

                    // Preserve value only if types are compatible
                    const val = isCompatible ? f.value : defaultValueForType(newType);

                    return {
                        ...f,
                        dataIndex: newColumn.dataIndex,
                        filterName: newColumn.filterName,
                        filterType: nextFilterType,
                        filterOptions: newColumn.filterOptions || [],
                        filterIncludeNull: newColumn.filterIncludeNull,
                        filterNullOptionLabel: newColumn.filterNullOptionLabel,
                        operator: op,
                        value: val,
                    };
                }

                return { ...f, [key]: value };
            })
        );
    };

    const removeFilter = (id: number) => setFilters((prev) => prev.filter((f) => f.id !== id));

    const clearFilters = () => {
        setFilters([]);
        tableUtils.removeFilters();
    };

    const applyFilters = () => {
        tableUtils.saveFilters();
    };

    return (
        <Card className="mt-4 rounded-lg w-full border-none bg-[#00000005]">
            <Row justify="space-between" align="middle">
                <Col>
                    <Typography.Title level={4}>{t("title")}</Typography.Title>
                </Col>
                <Col>
                    <Space>
                        <Button icon={<PlusOutlined />} onClick={addFilter}>
                            {tC("add")}
                        </Button>
                        <Button icon={<CloseOutlined />} onClick={clearFilters}>
                            {tC("cancel")}
                        </Button>
                        <Button type="primary" onClick={applyFilters}>
                            {tC("apply")}
                        </Button>
                    </Space>
                </Col>
            </Row>

            {filters.length > 0 && (
                <div className="mt-4 p-0">
                    {filters.map((filter) => {
                        // For each filter, show: string columns + non-string columns not used by OTHER filters
                        const otherFiltersNonStringDataIndices = filters
                            .filter((f) => f.id !== filter.id && f.filterType.type !== "string")
                            .map((f) => f.dataIndex);
                        const columnsForThisFilter = allFilterColumns.filter(
                            (col) =>
                                col.filterType.type === "string" ||
                                !otherFiltersNonStringDataIndices.includes(col.dataIndex)
                        );

                        return (
                            <div key={filter.id} className="mb-2">
                                <FilterItem
                                    filter={filter}
                                    updateFilter={updateFilter}
                                    removeFilter={removeFilter}
                                    availableFilterColumns={columnsForThisFilter}
                                />
                            </div>
                        );
                    })}
                </div>
            )}
        </Card>
    );
};

function defaultValueForType(type: string) {
    switch (type) {
        case "number":
            return 0;
        case "labelMultiSelect":
        case "countryMultiSelect":
        case "conversionStateSourceMultiSelect":
        case "conversionStateTypeMultiSelect":
        case "conversionStateStatusMultiSelect":
        case "integrationStatusMultiSelect":
        case "roleMultiSelect":
        case "offerSelect":
        case "partnerSelect":
        case "advertiserSelect":
        case "cascadeSelect":
            return [];
        case "date":
        case "datetime":
            return null;
        case "boolean":
            return undefined;
        default:
            return "";
    }
}
